const API_BASE = process.env.VUE_APP_API_BASE || 'http://localhost:8003'

function getToken() {
  return localStorage.getItem('token') || ''
}

function authHeaders() {
  const token = getToken()
  return token ? { 'Authorization': 'Bearer ' + token } : {}
}

function post(path, body) {
  return fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    credentials: 'include',
    body: JSON.stringify(body)
  }).then(res => res.json())
}

function get(path) {
  return fetch(`${API_BASE}${path}`, {
    method: 'GET',
    headers: { ...authHeaders() },
    credentials: 'include'
  }).then(res => res.json())
}

/**
 * 上传表单（multipart/form-data）。字段名固定为 file，与后端 @RequestParam("file") 对应。
 * 注意：不能手动设置 Content-Type，必须交给浏览器自动带上 boundary，否则后端解析失败。
 */
function postForm(path, file) {
  const form = new FormData()
  form.append('file', file)
  return fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { ...authHeaders() },
    credentials: 'include',
    body: form
  }).then(res => res.json())
}

function del(path) {
  return fetch(`${API_BASE}${path}`, {
    method: 'DELETE',
    headers: { ...authHeaders() },
    credentials: 'include'
  }).then(res => res.json())
}

/**
 * 用户登录
 * @param {string} account 账号（邮箱或手机号）
 * @param {string} password 密码
 * @returns {Promise<{code:number,message:string,data:{token:string,nickname:string,avatarUrl:string|null},success:boolean,timestamp:string}>}
 */
export function login(account, password) {
  return post('/api/v1/users/login', { account, password })
}

/**
 * 用户登出：注销服务端当前 token，立即清除 Redis 中的登录态。
 * 注意：本方法读取 localStorage 里的 token，必须在调用方清理本地缓存「之前」调用。
 * 前端本地缓存（token / user / taskblock-profile）需调用方另行清理。
 * @returns {Promise<{code:number,message:string,success:boolean}>}
 */
export function logout() {
  return post('/api/v1/users/logout', {})
}

/**
 * 发送邮箱验证码（统一发码接口）
 * @param {string} email 邮箱
 * @param {string} scene 场景：REGISTER 注册 / LOGIN 登录 / RESET_PASSWORD 找回密码 / CHANGE_EMAIL 修改邮箱 / BIND_EMAIL 绑定邮箱
 * @returns {Promise<{code:number,message:string,data:null,success:boolean,timestamp:string}>}
 */
export function sendEmailCode(email, scene) {
  return post('/api/v1/auth/email/code/send', { email, scene })
}

/**
 * 发送手机号验证码（统一发码接口）
 * @param {string} phone 手机号
 * @param {string} scene 场景：REGISTER 注册 / LOGIN 登录 / RESET_PASSWORD 找回密码 / CHANGE_PHONE 修改手机号 / BIND_PHONE 绑定手机号
 * @returns {Promise<{code:number,message:string,data:null,success:boolean,timestamp:string}>}
 */
function sendPhoneCode(phone, scene) {
  return post('/api/v1/auth/phone/code/send', { phone, scene })
}

/**
 * 用户注册
 * @param {Object} params 注册参数
 * @param {string} params.email 邮箱
 * @param {string} params.password 密码
 * @param {string} params.confirmPassword 确认密码
 * @param {string} params.verifyCode 验证码
 * @param {string} [params.nickname] 昵称
 * @param {string} [params.registerSource] 注册来源
 * @returns {Promise<{code:number,message:string,data:null,success:boolean,timestamp:string}>}
 */
export function register(params) {
  return post('/api/v1/users/register', {
    ...params,
    registerSource: params.registerSource || 'web'
  })
}

/**
 * 获取当前登录用户信息
 * @returns {Promise<{code:number,message:string,data:{id:number,phone:string,email:string,nickname:string,avatarUrl:string|null,status:string,timezone:string,language:string,lastLoginAt:string,createdAt:string}|null,success:boolean,timestamp:string}>}
 */
export function getMe() {
  return get('/api/v1/users/me')
}

/**
 * 发送更新资料验证码（修改邮箱/手机号时用）
 * 内部转调统一发码接口，根据 type 自动选择 scene。
 * @param {string} target 邮箱或手机号
 * @param {string} type 'email' 或 'phone'
 * @returns {Promise<{code:number,message:string,data:null,success:boolean,timestamp:string}>}
 */
export function sendUpdateVerifyCode(target, type) {
  if (type === 'email') {
    return sendEmailCode(target, 'CHANGE_EMAIL')
  }
  if (type === 'phone') {
    return sendPhoneCode(target, 'CHANGE_PHONE')
  }
  return Promise.reject(new Error('不支持的 type：' + type))
}

/**
 * 更新昵称（1-32 位）
 * 对应后端 POST /api/v1/users/nickname，DTO: UpdateNicknameCmd
 * @param {string} nickname 新昵称
 * @returns {Promise<{code:number,message:string,data:object,success:boolean,timestamp:string}>}
 */
export function updateNickname(nickname) {
  return post('/api/v1/users/nickname', { nickname })
}

/**
 * 修改邮箱（需新邮箱收到的验证码）
 * 对应后端 POST /api/v1/users/email/change，DTO: ChangeEmailCmd
 * 字段名为 newEmail（不是 email），发码请走 sendEmailCode(target, 'CHANGE_EMAIL')
 * @param {string} email 新邮箱
 * @param {string} verifyCode 新邮箱收到的 6 位验证码
 * @returns {Promise<{code:number,message:string,data:object,success:boolean,timestamp:string}>}
 */
export function updateEmail(email, verifyCode) {
  return post('/api/v1/users/email/change', { newEmail: email, verifyCode })
}

/**
 * 修改手机号（需新手机号收到的验证码）
 * 对应后端 POST /api/v1/users/phone/change，DTO: ChangePhoneCmd
 * 字段名为 newPhone（不是 phone），发码请走 sendPhoneCode(target, 'CHANGE_PHONE')
 * @param {string} phone 新手机号
 * @param {string} verifyCode 新手机号收到的 6 位验证码
 * @returns {Promise<{code:number,message:string,data:object,success:boolean,timestamp:string}>}
 */
export function updatePhone(phone, verifyCode) {
  return post('/api/v1/users/phone/change', { newPhone: phone, verifyCode })
}

/**
 * 修改密码（需原密码）
 * 对应后端 POST /api/v1/users/password，DTO: ChangePasswordCmd
 * 注意后端字段名为 oldPassword / newPassword / confirmNewPassword（不是 password）
 * 成功后后端会使该账号所有端下线（含当前端）
 * @param {string} oldPassword 原密码（8-32 位）
 * @param {string} newPassword 新密码（8-32 位，需同时包含字母和数字）
 * @param {string} confirmNewPassword 确认新密码，需与 newPassword 一致
 * @returns {Promise<{code:number,message:string,data:object,success:boolean,timestamp:string}>}
 */
export function updatePassword(oldPassword, newPassword, confirmNewPassword) {
  return post('/api/v1/users/password', { oldPassword, newPassword, confirmNewPassword })
}

/**
 * 上传头像到又拍云
 * 对应后端 POST /api/v1/files/avatar（multipart，字段名 file）
 * @param {File} file 头像图片文件
 * @returns {Promise<{code:number,message:string,data:string|null,success:boolean}>} data 为上传后的头像 URL
 */
export function uploadAvatar(file) {
  return postForm('/api/v1/files/avatar', file)
}

/**
 * 把已上传的头像 URL 写入用户资料（换下的旧头像由后端延迟清理）
 * 对应后端 POST /api/v1/users/avatar，DTO: UpdateAvatarCmd（字段名 avatarUrl）
 * @param {string} avatarUrl uploadAvatar 返回的头像 URL
 * @returns {Promise<{code:number,message:string,data:object,success:boolean}>}
 */
export function updateAvatar(avatarUrl) {
  return post('/api/v1/users/avatar', { avatarUrl })
}

/**
 * 获取当前用户已上传的背景图列表（最新在前，最多 3 张）
 * 对应后端 GET /api/v1/files/background
 * @returns {Promise<{code:number,message:string,data:string[]|null,success:boolean}>}
 */
export function listBackgrounds() {
  return get('/api/v1/files/background')
}

/**
 * 上传背景图（超过 3 张时后端自动替换最早上传的一张并删除其文件）
 * 对应后端 POST /api/v1/files/background（multipart，字段名 file）
 * @param {File} file 背景图片文件
 * @returns {Promise<{code:number,message:string,data:{current:string,backgrounds:string[]}|null,success:boolean}>}
 */
export function uploadBackground(file) {
  return postForm('/api/v1/files/background', file)
}

/**
 * 删除某张已上传的背景图（同时删除又拍云上的文件）
 * 对应后端 DELETE /api/v1/files/background?url=...
 * @param {string} url 背景图 URL
 * @returns {Promise<{code:number,message:string,data:string[]|null,success:boolean}>} data 为删除后剩余的背景图列表
 */
export function deleteBackground(url) {
  return del('/api/v1/files/background?url=' + encodeURIComponent(url))
}

/**
 * 注销当前账号（不可逆）
 * 对应后端 POST /api/v1/users/me/delete，DTO: DeleteAccountCmd（字段名 password）
 *
 * 后端行为：软删除 + 匿名化（手机号/邮箱/密码哈希/头像清空，昵称改为「已注销用户」），
 * 并踢掉该账号的所有会话。成功后本地必须立刻清登录态，否则后续请求全是 401。
 * @param {string} password 当前登录密码，用于确认是本人操作
 * @returns {Promise<{code:number,message:string,data:null,success:boolean}>}
 */
export function deleteAccount(password) {
  return post('/api/v1/users/me/delete', { password })
}
